/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faUser, faLock, faArrowRight, faUsers, faBriefcase, faChartLine, faShieldAlt, faHome } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import logoImage from '../assets/FINAL LOGO AK.png';
import AuthMobileAurora from '../components/AuthMobileAurora';
import '../styles/Login.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002/api';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [platformStats, setPlatformStats] = useState({
    activeJobs: null,
    companies: null,
    jobSeekers: null
  });
  const [statsLoading, setStatsLoading] = useState(true);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // The login function in AuthContext will handle role-based routing
      await login(formData.email, formData.password);
      // No need to navigate here - AuthContext handles it based on role
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setError('');
      setLoading(true);

      const response = await fetch(buildApiUrl('/api/auth/google/auth'));
      const data = await response.json();

      if (response.ok && data.auth_url) {
        // Redirect user to Google's OAuth consent screen
        window.location.href = data.auth_url;
      } else {
        setError(data.error || 'Unable to start Google sign-in. Please try again.');
      }
    } catch (e) {
      console.error('Google login error', e);
      setError('Unable to start Google sign-in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const leftVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        delay: 0.2
      }
    }
  };

  const rightVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        delay: 0.4
      }
    }
  };

  const buttonVariants = {
    hover: {
      scale: 1.005,
      transition: {
        duration: 0.2
      }
    },
    tap: {
      scale: 0.995
    }
  };

  // Format statistics numbers with K/M suffixes
  const formatStatValue = (value, addPlus = true) => {
    if (value === null || value === undefined) {
      return statsLoading ? '...' : '0';
    }

    const numericValue = Number(value) || 0;

    if (numericValue >= 1_000_000) {
      const formatted = (numericValue / 1_000_000).toFixed(numericValue >= 10_000_000 ? 0 : 1).replace(/\.0$/, '');
      return `${formatted}M${addPlus && numericValue > 0 ? '+' : ''}`;
    }

    if (numericValue >= 1_000) {
      const formatted = (numericValue / 1_000).toFixed(numericValue >= 10_000 ? 0 : 1).replace(/\.0$/, '');
      return `${formatted}K${addPlus && numericValue > 0 ? '+' : ''}`;
    }

    return `${numericValue.toLocaleString()}${addPlus && numericValue > 0 ? '+' : ''}`;
  };

  // Fetch platform statistics
  useEffect(() => {
    const fetchPlatformStats = async () => {
      try {
        setStatsLoading(true);
        const response = await fetch(buildApiUrl('/api/public/stats-summary'));
        if (!response.ok) {
          throw new Error(`Failed to fetch stats: ${response.status}`);
        }

        const payload = await response.json();
        if (payload?.success && payload.data) {
          const data = payload.data;
          setPlatformStats({
            activeJobs: data.activeJobs ?? 0,
            companies: data.companies ?? 0,
            jobSeekers: data.jobSeekers ?? 0
          });
        } else {
          console.warn('Stats summary did not return success flag.', payload);
        }
      } catch (error) {
        console.error('Error fetching platform stats:', error);
        // Keep default values on error
      } finally {
        setStatsLoading(false);
      }
    };

    fetchPlatformStats();
  }, []);

  // Build benefits array with real statistics
  const benefits = [
    {
      icon: faUsers,
      title: `${formatStatValue(platformStats.activeJobs)} Active Jobs`,
      description: "Find opportunities across all industries"
    },
    {
      icon: faBriefcase,
      title: `${formatStatValue(platformStats.companies)} Companies`,
      description: "Connect with top employers worldwide"
    },
    {
      icon: faChartLine,
      title: `${formatStatValue(platformStats.jobSeekers)} Job Seekers`,
      description: "Join our thriving professional community"
    },
    {
      icon: faShieldAlt,
      title: "100% Verified",
      description: "All jobs and companies are verified"
    }
  ];

  return (
    <div className="login-container">
      <AuthMobileAurora />

      <div className="login-mobile-header">
        <header className="login-top-bar">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="back-to-home-btn"
          >
            <FontAwesomeIcon icon={faHome} />
            Back to Home
          </button>
        </header>

        <div className="login-mobile-branding">
          <img src={logoImage} alt="AksharJobs Logo" className="login-mobile-logo" />
          <h1 className="login-mobile-title">AksharJobs</h1>
          <p className="login-mobile-tagline">CONNECT | DISCOVER | ELEVATE</p>
        </div>
      </div>

      <motion.div 
        className="login-layout"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Left Side - Why Choose AksharJobs */}
        <motion.div 
          className="login-left"
          variants={leftVariants}
        >
          <div className="left-content">
            <div className="benefits-section">
              <img src={logoImage} alt="AksharJobs Logo" className="main-logo" />
              <p className="tagline">CONNECT | DISCOVER | ELEVATE</p>
              <h2>Why Choose AksharJobs?</h2>
              <div className="benefits-grid">
                {benefits.map((benefit, index) => (
                  <motion.div 
                    key={index}
                    className="benefit-item"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 + (index * 0.15), duration: 0.5 }}
                  >
                    <div className="benefit-icon">
                      <FontAwesomeIcon icon={benefit.icon} />
                    </div>
                    <div className="benefit-content">
                      <h3>{benefit.title}</h3>
                      <p>{benefit.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Right Side - Login Form */}
        <motion.div 
          className="login-right"
          variants={rightVariants}
        >
          <div className="login-card">
            <div className="login-header">
              <h2>Welcome Back!</h2>
              <p>Sign in to access your account</p>
            </div>

            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                <div className="label-group">
                  <FontAwesomeIcon icon={faUser} className="form-label-icon" />
                  <label htmlFor="email">Email Address</label>
                </div>
                <div className="input-wrapper">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                    className={error ? 'error' : ''}
                  />
                </div>
              </div>

              <div className="form-group">
                <div className="label-group">
                  <FontAwesomeIcon icon={faLock} className="form-label-icon" />
                  <label htmlFor="password">Password</label>
                </div>
                <div className="input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    required
                    className={error ? 'error' : ''}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
              </div>

              {error && (
                <motion.div 
                  className="error-message"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {error}
                </motion.div>
              )}

              <div className="form-options">
                <label className="checkbox-wrapper remember-me" htmlFor="rememberMe">
                  <input type="checkbox" id="rememberMe" name="rememberMe" />
                  <span className="checkmark"></span>
                  Remember me
                </label>
                <Link to="/forgot-password" className="forgot-link">
                  Forgot Password?
                </Link>
              </div>

              <motion.button
                type="submit"
                className={`login-button${loading ? ' is-loading' : ''}`}
                disabled={loading}
                variants={buttonVariants}
                whileHover={loading ? undefined : 'hover'}
                whileTap={loading ? undefined : 'tap'}
                aria-busy={loading}
              >
                {loading ? (
                  <span className="login-button-loader" role="status" aria-live="polite">
                    <span className="login-button-loader__ring" aria-hidden="true" />
                    <span className="login-button-loader__label">Signing in</span>
                  </span>
                ) : (
                  <>
                    Sign In
                    <FontAwesomeIcon icon={faArrowRight} />
                  </>
                )}
              </motion.button>
            </form>

            <div className="login-oauth-section">
              <div className="oauth-divider">
                <span>or</span>
              </div>
              <motion.button
                type="button"
                className="google-login-button"
                onClick={handleGoogleLogin}
                disabled={loading}
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <span className="google-login-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 48 48"
                    style={{ width: 18, height: 18 }}
                  >
                    <path
                      fill="#EA4335"
                      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.2C12.27 13.72 17.7 9.5 24 9.5z"
                    />
                    <path
                      fill="#34A853"
                      d="M46.98 24.55c0-1.57-.14-3.08-.39-4.55H24v9.02h12.94c-.56 2.9-2.26 5.36-4.82 7.01l7.79 6.04C44.93 37.67 46.98 31.62 46.98 24.55z"
                    />
                    <path
                      fill="#4285F4"
                      d="M9.54 28.58A14.5 14.5 0 0 1 8.83 24c0-1.59.27-3.12.75-4.55l-7.02-5.47A23.86 23.86 0 0 0 0 24c0 3.82.92 7.42 2.56 10.6l6.98-6.02z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M24 48c6.48 0 11.93-2.13 15.9-5.79l-7.79-6.04C29.9 37.68 27.17 38.5 24 38.5c-6.29 0-11.63-4.21-13.53-9.92l-7.98 6.02C6.51 42.62 14.62 48 24 48z"
                    />
                  </svg>
                </span>
                <span className="google-login-text">Continue with Google</span>
              </motion.button>
            </div>

            <div className="login-footer">
              <p>
                Don't have an account? 
                <Link to="/signup" className="signup-link"> Sign up</Link>
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Login;
